import { useEffect, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box, Link, Text } from '@factorialco/deprecated-design-system'
import { F0Card, F0Dialog } from '@factorialco/f0-react'
import { ResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Calendar, Upsell } from '@factorialco/f0-react/icons/app'
import { AvailableModules, Features } from 'generated/featureDefinitions'
import {
  OneOnOneEmployeeInfoItemFragment,
  OneOnOneMeetingInfoDocument,
} from 'generated/resources/graphql'
import moment from 'moment'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import buildUrl from 'modules/core/lib/buildUrl'
import i18n from 'modules/core/lib/i18n'

import { CreateOneOnOneModal } from 'modules/meetings/components/CreateOneOnOneModal'
import { PrivacyModal } from 'modules/meetings/components/PrivacyModal'
import { formatDateToString, formatTimeToString } from 'modules/meetings/lib/utils/dates'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'
import { UpsellingKit } from 'modules/upselling_kit/components'

interface EmployeeHeaderProps {
  employee: OneOnOneEmployeeInfoItemFragment | undefined
  canSeeAdvancedMeetings: boolean
}

const EmployeeHeader = ({ employee, canSeeAdvancedMeetings }: EmployeeHeaderProps) => {
  const navigate = useNavigate()

  const [openCreateScheduledMeeting, setOpenCreateScheduledMeeting] = useState(false)
  const [openChooseFormatModal, setOpenChooseFormatModal] = useState(false)
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false)
  const [openPrivacyPolicy, setOpenPrivacyPolicy] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const {
    employee: currentEmployee,
    access: { locale },
  } = useCurrentSession()
  const { meetingId } = useParams<{ meetingId: string }>()
  const { navigation } = useNavigation()

  const { data: meetingData } = useQuery(OneOnOneMeetingInfoDocument, {
    variables: { id: String(meetingId) },
    skip: !meetingId,
  })

  useEffect(() => {
    if (searchParams.get('schedule') === 'true') {
      setOpenCreateScheduledMeeting(true)
    }
  }, [searchParams])

  const meeting = meetingData?.meetings?.meeting ?? null

  const joinRoomUrl = meetingId
    ? navigation.engagement.one_on_ones.employeeId.meetingId.prejoin.fullPath({
        meetingId,
        employeeId: employee?.id ?? '',
      })
    : '/'

  const joinRecordUrl = meetingId
    ? navigation.engagement.one_on_ones.employeeId.meetingId.record.fullPath({
        meetingId,
        employeeId: employee?.id ?? '',
      })
    : '/'

  if (!employee || employee.id === currentEmployee?.id) return null

  return (
    <>
      <ResourceHeader
        avatar={{
          firstName: employee?.firstName ?? '',
          lastName: employee?.lastName ?? '',
          src: buildUrl(employee?.avatar?.url ?? ''),
          type: 'person',
        }}
        description={employee?.referenceContract?.jobTitle ?? ''}
        metadata={[
          employee?.manager && {
            label: i18n.t('meetings.one_on_ones.employee_header.manager'),
            value: {
              text: employee.manager.fullName,
              type: 'avatar' as const,
              variant: {
                firstName: employee.manager.firstName,
                lastName: employee.manager.lastName,
                src: buildUrl(employee.manager.avatar?.url ?? ''),
                type: 'person' as const,
              },
            },
          },
          {
            label: i18n.t('meetings.one_on_ones.employee_header.team'),
            value: {
              data:
                employee?.memberships && employee?.memberships.length > 0
                  ? employee.memberships.map((membership) => membership.team?.name ?? '')
                  : [i18n.t('meetings.one_on_ones.employee_header.no_team')],
              type: 'data-list' as const,
            },
          },
          meeting && {
            label: i18n.t('meetings.one_on_ones.employee_header.date_time'),
            value: {
              content: `${formatDateToString(meeting?.startTime, locale)} · ${formatTimeToString(
                meeting?.startTime,
                locale
              )} - ${formatTimeToString(meeting?.endTime, locale)}`,
              type: 'text' as const,
            },
          },
          meeting?.template?.frequency && {
            label: i18n.t('meetings.one_on_ones.employee_header.frequency.title'),
            value: {
              content:
                meeting?.template?.frequency === 'weekly'
                  ? i18n.t('meetings.one_on_ones.employee_header.frequency.weekly', {
                      day: moment(meeting?.startTime).locale(locale).format('dddd'),
                    })
                  : i18n.t('meetings.one_on_ones.employee_header.frequency.monthly'),
              type: 'text' as const,
            },
          },
        ].filter(Boolean)}
        title={employee?.fullName ?? ''}
        primaryAction={{
          label: i18n.t('meetings.one_on_ones.employee_header.go_to_meeting'),
          onClick: () => setOpenChooseFormatModal(true),
          isVisible: Boolean(meetingId && canSeeAdvancedMeetings),
        }}
        secondaryActions={[
          {
            label: i18n.t('meetings.one_on_ones.employee_header.new_one_on_one'),
            icon: Calendar,
            onClick: () => setOpenCreateScheduledMeeting(true),
          },
          {
            label: i18n.t('meetings.one_on_ones.upsell.header.button'),
            onClick: () => setIsUpsellModalOpen(true),
            variant: 'promote' as const,
            icon: Upsell,
            isVisible: !canSeeAdvancedMeetings,
          },
        ]}
        otherActions={[
          {
            label: i18n.t('meetings.one_on_ones.employee_header.view_profile'),
            onClick: () => {
              navigate(navigation.employees.id.fullPath({ id: employee.id }))
            },
          },
        ].filter(Boolean)}
      />

      {openCreateScheduledMeeting && (
        <CreateOneOnOneModal
          defaultSelectedEmployeeId={employee?.id}
          setOpenModal={(open) => {
            setOpenCreateScheduledMeeting(open)
            const newSearchParams = new URLSearchParams(searchParams)
            newSearchParams.delete('schedule')
            setSearchParams(newSearchParams, { replace: true })
          }}
          canSeeAdvancedMeetings={canSeeAdvancedMeetings}
        />
      )}

      {openChooseFormatModal && !openPrivacyPolicy && (
        <F0Dialog
          isOpen={openChooseFormatModal}
          onClose={() => setOpenChooseFormatModal(false)}
          title={i18n.t('meetings.one_on_ones.employee_header.select_format.title')}
        >
          <Box gap="s16" flexDirection="row">
            <F0Card
              avatar={{
                emoji: '🎙️',
                type: 'emoji',
              }}
              title={i18n.t('meetings.one_on_ones.employee_header.select_format.presential.title')}
              description={i18n.t(
                'meetings.one_on_ones.employee_header.select_format.presential.description'
              )}
              link={joinRecordUrl}
            />
            <F0Card
              avatar={{
                emoji: '💻',
                type: 'emoji',
              }}
              title={i18n.t('meetings.one_on_ones.employee_header.select_format.videocall.title')}
              description={i18n.t(
                'meetings.one_on_ones.employee_header.select_format.videocall.description'
              )}
              link={joinRoomUrl}
            />
          </Box>
          <Box flexDirection="row" gap="s6" style={{ marginTop: '16px' }}>
            <Text.Normal size="200">{i18n.t('meetings.prejoin.privacy.description')}</Text.Normal>
            <Link.Touchable type="default" onClick={() => setOpenPrivacyPolicy(true)}>
              {i18n.t('meetings.prejoin.privacy.link')}
            </Link.Touchable>
          </Box>
        </F0Dialog>
      )}

      {isUpsellModalOpen && (
        <UpsellingKit.ProductModal
          isOpen={isUpsellModalOpen}
          onClose={() => setIsUpsellModalOpen(false)}
          pageSource="employee-meetings-header"
          requestSource="employee-meetings-header"
          requestReason={ValidReasons.MissingFeature}
          moduleRequested={AvailableModules.engagement}
          featureRequested={Features.engagement_one_on_one_meetings}
        />
      )}

      {openPrivacyPolicy && (
        <PrivacyModal onOpen={openPrivacyPolicy} onClose={() => setOpenPrivacyPolicy(false)} />
      )}
    </>
  )
}

export default EmployeeHeader
